import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
    CompareContractsInput,
    CompareContractsOutput,
    DeactivateContractsInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import externalLinkTokens from '@amzn/meridian-tokens/base/icon/export-large';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import viewComparisonTokens from '@amzn/meridian-tokens/base/icon/search';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isUndefined } from 'lodash';
import { useHistory } from 'react-router';
import { contractConfigurationClient } from 'src/api/client';
import { ROUTES } from 'src/constants/routes';
import { COPY_FROM_TEMPLATE_ISSUE, SIM_CREATE_URL } from 'src/constants/ticketing';
import { AppContext } from 'src/contexts/appContext';

const DEFAULT_REDIRECT_TIMEOUT = 1000;

export interface IValidateNewRateCardModal {
    /**
     * The ID of the newly created contract to validate.
     */
    newContractId?: number;

    setNewContractId: Dispatch<SetStateAction<number | undefined>>;

    /**
     * The ID of the template used to create the new contract.
     */
    templateContractId?: number;

    setTemplateContractId: Dispatch<SetStateAction<number | undefined>>;

    /**
     * Indicates if the previous modal is still open.
     */
    isAddRateCardModalOpen: boolean;

    /**
     * The duration in milliseconds that the component will wait before redirecting
     * to the edit screen if the validation succeeds.
     *
     * This will provide some additional feedback to the user that the validation
     * succeeded.
     */
    redirectTimeout?: number;
}

/**
 * Component that compares newly created contracts with their source template.
 *
 * New contracts are expected to exactly match their template. If any difference
 * is detected, this component will require the user to acknowledge that some
 * part of the copy operation failed before they can proceed to the edit screen.
 *
 * @param templateContractId The ID of the template used to create the new contract.
 * @param setTemplateContractId State setter for template contract ID. Used to clear the state.
 * @param newContractId The ID of the newly created contract to validate.
 * @param setNewContractId State setter for the new contract ID. Used to clear the state.
 * @param isAddRateCardModalOpen Indicates if the previous modal is still open.
 * @param redirectTimeout The time the component will wait before redirecting to
 * a successfully validated contract.
 */
export const ValidateNewRateCardModal: React.FC<IValidateNewRateCardModal> = ({
    templateContractId,
    setTemplateContractId,
    newContractId,
    setNewContractId,
    isAddRateCardModalOpen,
    redirectTimeout = DEFAULT_REDIRECT_TIMEOUT
}: IValidateNewRateCardModal) => {
    const history = useHistory();
    const { userInfo } = React.useContext(AppContext);
    const [isValidating, setIsValidating] = useState(false);
    const [shouldOpen, setShouldOpen] = useState(false);
    const [validationFailed, setValidationFailed] = useState<boolean | undefined>();
    const [validationErrored, setValidationErrored] = useState(false);
    const proceedToEdit = useCallback(() => {
        setTemplateContractId(undefined);
        setNewContractId(undefined);
        history.push(`${ROUTES.RATE_CARDS.INDEX}${ROUTES.RATE_CARDS.ADD}/${newContractId}`);
    }, [newContractId, setNewContractId, setTemplateContractId]);

    useEffect(() => {
        if (isValidating || !isUndefined(validationFailed) || validationErrored) {
            setShouldOpen(true);
        }
    }, [isValidating, validationFailed, validationErrored]);

    const canValidate = !isUndefined(templateContractId) && !isUndefined(newContractId);

    function validate() {
        if (canValidate) {
            setIsValidating(true);

            contractConfigurationClient
                .compareContracts(
                    CompareContractsInput.clone({
                        left: { contractId: templateContractId },
                        right: { contractId: newContractId },
                        includeMatchingRows: false,
                        pageNumber: 0,
                        pageSize: 1
                    })
                )
                .subscribe({
                    next: (response: CompareContractsOutput) => {
                        const didSucceed = response.deltas.length === 0;
                        setValidationFailed(!didSucceed);
                        if (didSucceed) {
                            setTimeout(() => proceedToEdit(), redirectTimeout);
                        } else {
                            contractConfigurationClient.deactivateContracts(
                                DeactivateContractsInput.clone({
                                    contractIds: [newContractId],
                                    changeBy: userInfo.username
                                })
                            );
                        }
                    },
                    error: (e: UnknownCoralError) => {
                        setIsValidating(false);
                        setValidationErrored(true);
                    },
                    complete: () => setIsValidating(false)
                });
        }
    }

    useEffect(() => validate(), [templateContractId, newContractId]);

    const loadingComponents = (
        <Alert type={'neutral'}>
            <Row widths={['fit', 'fill']}>
                <Text>Validating new contract</Text>
                <Loader type={'linear'} size={'large'} />
            </Row>
        </Alert>
    );

    const succeededComponents = <Alert type={'success'}>Validation succeeded</Alert>;

    const failedComponents = (
        <>
            <Alert type={'error'}>Validation failed</Alert>
            <Row alignmentHorizontal={'justify'} widths={'fill'}>
                <Button onClick={() => setShouldOpen(false)} type={'secondary'}>
                    Go back
                </Button>
                <Button
                    href={`.${ROUTES.COMPARE.CONTRACTS}?leftId=${templateContractId}&rightId=${newContractId}`}
                    target={'_blank'}
                    type={'tertiary'}
                >
                    Show Validation
                    <Icon tokens={viewComparisonTokens} />
                </Button>
                <TicketButton newContractId={newContractId} templateContractId={templateContractId} />
            </Row>
        </>
    );

    const erroredComponents = (
        <>
            <Alert type={'warning'}>Error running validation</Alert>
            <Row alignmentHorizontal={'justify'} widths={'fill'}>
                <Button onClick={() => setShouldOpen(false)} type={'secondary'}>
                    Go back
                </Button>
                <Button onClick={() => validate()}>
                    Retry validation
                    <Icon tokens={refreshTokens} />
                </Button>
                <TicketButton newContractId={newContractId} templateContractId={templateContractId} />
            </Row>
        </>
    );

    const problemComponents = validationErrored ? erroredComponents : failedComponents;

    const doneComponents = validationErrored || validationFailed ? problemComponents : succeededComponents;

    return (
        <Modal open={shouldOpen && !isAddRateCardModalOpen} aria-role={'validation-modal'}>
            <Column>{isValidating ? loadingComponents : doneComponents}</Column>
        </Modal>
    );
};

interface ITicketButton {
    newContractId?: number;
    templateContractId?: number;
}

const TicketButton: React.FC<ITicketButton> = ({ newContractId, templateContractId }: ITicketButton) => {
    const ticketLink = useMemo(() => {
        const url = new URL(SIM_CREATE_URL);
        url.searchParams.set('title', `Failed to copy contract ${newContractId} from ${templateContractId}`);
        url.searchParams.set('category', COPY_FROM_TEMPLATE_ISSUE.CATEGORY);
        url.searchParams.set('type', COPY_FROM_TEMPLATE_ISSUE.TYPE);
        url.searchParams.set('item', COPY_FROM_TEMPLATE_ISSUE.ITEM);
        url.searchParams.set('severity', COPY_FROM_TEMPLATE_ISSUE.SEVERITY);
        return url.toString();
    }, [newContractId, templateContractId]);

    return (
        <Button href={ticketLink.toString()} target={'_blank'}>
            Cut a ticket to RAD
            <Icon tokens={externalLinkTokens} />
        </Button>
    );
};
